import type {
  MRT_ColumnSizingState,
  MRT_RowData,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
} from "material-react-table";
import { useCallback, useRef, type MouseEvent as ReactMouseEvent, type RefObject } from "react";

/**
 * How a column says which column it is, to the DOM.
 *
 * MRT stamps its cells with a positional `data-index` and nothing else, so a column's cells cannot be
 * found by the column they belong to — which is what measuring one column's longest content needs.
 * Every table that offers resizing spreads {@link columnWidthCellProps} into its head and body cell
 * props, and this is the attribute that comes out.
 */
export const COLUMN_ID_ATTRIBUTE = "data-column-id";

/**
 * Marks a value another column folded onto this cell's second line (#6711).
 *
 * The fit measures a column by its own content. A folded value belongs to the column it came from, and
 * truncates in whatever room its host has, so it is never measured as the host's: a host that grew with
 * every value folded into it would push the fit to fold further, and unfold again on the next measure.
 */
export const FOLDED_VALUE_ATTRIBUTE = "data-folded-value";

/** How long after the first tap a second one still counts as a double tap, in milliseconds. */
const DOUBLE_TAP_WINDOW = 400;

/** What a column is never narrowed below by a double-click, whatever its content measures. */
const MINIMUM_COLUMN_WIDTH = 60;

/**
 * What a double-click adds to the width it measured.
 *
 * A cell's content is measured to the pixel, and a column sized to exactly that puts the last glyph
 * against the cell's edge — where the ellipsis rule then trims it, so the column reads as still cut
 * off after being asked to show everything. The allowance is the gap that makes "fits" look like it.
 */
const FULL_WIDTH_ALLOWANCE = 8;

/**
 * The MRT options that make a table's columns resizable.
 *
 * `layoutMode` is stated rather than left to MRT, which would otherwise pick `grid-no-grow` the moment
 * resizing is enabled: `grid` gives each column `flex: <size> 0 auto`, so the columns share out any
 * slack in proportion to the widths they asked for and overflow — rather than leaving a gap at the
 * right — when there is none. `grid-no-grow` does the opposite and parks dead space beside the last
 * column on a wide pane.
 *
 * `onChange` is MRT's own default resize mode and is stated for the same reason: the column follows
 * the pointer while the reader drags it, rather than jumping to its new width on release.
 */
export function resizableColumnOptions<TData extends MRT_RowData>(): Partial<MRT_TableOptions<TData>> {
  return {
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    layoutMode: "grid",
  };
}

/** The attribute a head or body cell carries so its column can find it. Spread into the cell's props. */
export function columnWidthCellProps(columnId: string): Record<string, string> {
  return { [COLUMN_ID_ATTRIBUTE]: columnId };
}

/**
 * How wide one cell's content actually is, including the padding around it.
 *
 * Not `scrollWidth` on the cell. These cells hide their overflow and hold a full-width span that
 * ellipsises inside them, so the cell's own scroll width is its client width and says nothing about
 * the text — the span underneath is what knows. Descendants are measured from their own left edge, so
 * a Name cell reports the avatar's width PLUS the name beside it rather than the wider of the two.
 */
export function cellContentWidth(cell: HTMLElement): number {
  const style = getComputedStyle(cell);
  const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(style.paddingRight) || 0;
  const cellLeft = cell.getBoundingClientRect().left + paddingLeft;

  let content = cell.scrollWidth - paddingLeft;
  for (const descendant of cell.querySelectorAll<HTMLElement>("*")) {
    const reach = descendant.getBoundingClientRect().left - cellLeft + descendant.scrollWidth;
    if (reach > content) content = reach;
  }

  return content + paddingLeft + paddingRight;
}

/**
 * How wide one cell's content is of its own accord, its padding included, whatever width the cell has.
 *
 * {@link cellContentWidth} answers "how wide must this cell be to show everything", and never less than
 * the cell already is: a cell's own scroll width is its client width, and so is that of any block inside
 * it. That is right for a double-click, which only ever widens. A fit has to know how *narrow* a column
 * can be, so this reaches only as far as the content does: each run of text, each element narrower than
 * or wider than the cell (a chip, a button, an avatar), and each element that overflows — a truncated span, whose
 * scroll width is its whole text. A block stretched to the cell's width says nothing about its content,
 * so only what is inside it counts; an inline box, or one its own min-width holds as wide as the cell,
 * is content, and counts. Measured from the cell's content edge, so content beside other
 * content counts in full — or from wherever the content starts, if that is further left: a cell that
 * centers controls wider than itself spills them over both of its edges. A value another column folded
 * into the cell is left out ({@link FOLDED_VALUE_ATTRIBUTE}).
 */
export function cellIntrinsicWidth(cell: HTMLElement): number {
  const style = getComputedStyle(cell);
  const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(style.paddingRight) || 0;
  const cellBox = cell.getBoundingClientRect();
  const contentLeft = cellBox.left + paddingLeft;
  const stretchedWidth = cellBox.width - paddingLeft - paddingRight;

  let start = contentLeft;
  let end = contentLeft;
  const reachOver = (left: number, right: number) => {
    start = Math.min(start, left);
    end = Math.max(end, right);
  };
  const range = document.createRange();
  const walker = document.createTreeWalker(cell, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node instanceof Element && node.hasAttribute(FOLDED_VALUE_ATTRIBUTE) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!node.textContent?.trim()) continue;
      range.selectNodeContents(node);
      const text = range.getBoundingClientRect();
      reachOver(text.left, text.right);
      continue;
    }
    const box = (node as Element).getBoundingClientRect();
    const { scrollWidth, clientWidth } = node as Element;
    if (scrollWidth > clientWidth) reachOver(box.left, box.left + scrollWidth);
    // Only a block that starts and ends where the cell's content does is stretched to it. A button as
    // wide as a narrow cell, or wider, and centered over its edges, is content, and counts. So does a
    // control that fills the content box by chance: an inline box, which layout never stretches, or a
    // box held at that width by its own min-width, as a phone's 44px row action is. Skipping one of
    // those narrows the column until the control overflows, which counts it again — and the column
    // then flips between the two widths for as long as it is on screen (#7015).
    const isStretched =
      Math.abs(box.left - contentLeft) < 0.5 &&
      Math.abs(box.width - stretchedWidth) < 0.5 &&
      fillsCellOnlyByStretching(node as Element, box.width);
    if (box.width > 0 && !isStretched) reachOver(box.left, box.right);
  }

  const reach = end - start;
  return reach > 0 ? reach + paddingLeft + paddingRight : 0;
}

/**
 * Whether a box as wide as its cell's content could only be that wide because layout stretched it:
 * not inline, which layout never stretches, and not held at that width by its own min-width.
 */
function fillsCellOnlyByStretching(element: Element, width: number): boolean {
  const style = getComputedStyle(element);
  if (style.display.startsWith("inline")) return false;
  const minimumWidth = Number.parseFloat(style.minWidth);
  return !(minimumWidth >= width - 0.5);
}

/**
 * How wide one column would have to be for none of its cells to be cut off — its header included.
 *
 * Zero where nothing can be measured, which is every renderer without layout: jsdom reports every box
 * as empty, and a column sized from that would collapse. The callers treat zero as "do not resize".
 */
export function measureColumnContentWidth(container: HTMLElement | null, columnId: string): number {
  if (!container) return 0;

  let widest = 0;
  for (const cell of container.querySelectorAll<HTMLElement>(`[${COLUMN_ID_ATTRIBUTE}="${columnId}"]`)) {
    const width = cellContentWidth(cell);
    if (width > widest) widest = width;
  }

  return widest > 0 ? Math.ceil(widest) + FULL_WIDTH_ALLOWANCE : 0;
}

export type ColumnWidthToggle = {
  /** The column's new width, or `undefined` to put it back on the width the table sizes it at. */
  width: number | undefined;
  /** Whether the column is now held at its full content width. */
  expanded: boolean;
};

/**
 * What a double-click on a column does to its width: out to its longest content, then back.
 *
 * A column already held open goes back to the width the table gives it, whatever the measurement says
 * — so a second double-click always restores, even where the content has changed in between. A column
 * that measures nothing (a renderer with no layout, an empty column) is left exactly as it is rather
 * than collapsed to the floor.
 */
export function toggleColumnWidth(
  params: Readonly<{ expanded: boolean; contentWidth: number; currentWidth?: number }>,
): ColumnWidthToggle {
  if (params.expanded) return { width: undefined, expanded: false };
  if (params.contentWidth <= 0) return { width: params.currentWidth, expanded: false };
  return { width: Math.max(params.contentWidth, MINIMUM_COLUMN_WIDTH), expanded: true };
}

/**
 * Applies one column's toggled width to MRT's column-sizing state.
 *
 * An `undefined` width REMOVES the entry rather than storing one, because a column with no entry is a
 * column the table sizes itself — which is what "back to its default width" means. Written as a plain
 * function over the state so a table whose sizing is computed from its pane (the document collections
 * fit theirs to the panel they are given) can layer the reader's choice over its own without either
 * one being lost.
 */
export function applyColumnWidth(
  sizing: MRT_ColumnSizingState,
  columnId: string,
  width: number | undefined,
): MRT_ColumnSizingState {
  if (width === undefined) {
    if (!(columnId in sizing)) return sizing;
    const remaining = { ...sizing };
    delete remaining[columnId];
    return remaining;
  }
  return { ...sizing, [columnId]: width };
}

export type DoubleTapState = {
  columnId: string;
  at: number;
};

/**
 * Whether this tap completes a double tap on the same column, and what to remember for the next one.
 *
 * Touch is the other half of "double-click to toggle". A double tap does not reliably reach a page as
 * a `dblclick` — mobile browsers hold the second tap back while they decide whether it is a zoom — so
 * the pair is recognised here from the taps themselves.
 */
export function readDoubleTap(
  previous: DoubleTapState | undefined,
  tap: DoubleTapState,
): { isDoubleTap: boolean; next: DoubleTapState | undefined } {
  const isDoubleTap =
    previous !== undefined && previous.columnId === tap.columnId && tap.at - previous.at <= DOUBLE_TAP_WINDOW;
  return { isDoubleTap, next: isDoubleTap ? undefined : tap };
}

/**
 * Whether a click is the second of a double-click, and so belongs to the width toggle rather than to
 * whatever it landed on.
 *
 * MRT hangs a column's sort toggle off the label inside the header, so without this a double-click
 * meant for the width would sort the column twice on its way there. The pair's FIRST click still
 * reaches the label — {@link ColumnWidthHandlers} puts the sorting back when the toggle fires.
 */
export function isSecondClickOfDoubleClick(detail: number): boolean {
  return detail >= 2;
}

export type ColumnWidthHandlers = {
  sortingBeforeClick: MRT_SortingState | undefined;
  suppressNextClick: boolean;
  doubleTap: DoubleTapState | undefined;
};

export const NO_COLUMN_WIDTH_HANDLERS: ColumnWidthHandlers = {
  sortingBeforeClick: undefined,
  suppressNextClick: false,
  doubleTap: undefined,
};

/**
 * A table's resizable-column mechanics: the container its content is measured in, and the head and
 * body cell props that stamp each cell with its column and turn a double-click or a double tap on a
 * header into the width toggle.
 *
 * The widths themselves are MRT's own `columnSizing` state, reached through the table instance, so a
 * table that computes its own widths only has to keep wiring `onColumnSizingChange` — the reader's
 * choice layers over whatever that table works out for itself, and removing the entry takes it off
 * again.
 *
 * MRT takes one ref for its table container. A table that already hands it a ref of its own passes
 * that ref here, and the content is measured through it; otherwise the hook makes its own, which the
 * table hands to MRT. Where neither holds an element, the content is measured in MRT's own container.
 *
 * `isHeldByReader` is for a table whose widths are fitted (`useFittedColumns`): a column the reader
 * dragged is held at the reader's width, and a double-click on it returns it to the fitted width rather
 * than opening it out to its content (#6708).
 */
export function useResizableColumns<TData extends MRT_RowData>(
  sharedContainerRef?: RefObject<HTMLDivElement | null>,
  isHeldByReader?: (columnId: string) => boolean,
) {
  const ownContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = sharedContainerRef ?? ownContainerRef;
  const expandedColumns = useRef<Set<string>>(new Set());
  const interaction = useRef<ColumnWidthHandlers>(NO_COLUMN_WIDTH_HANDLERS);

  const toggleWidth = useCallback((columnId: string, table: MRT_TableInstance<TData>) => {
    // A column the table will not let a reader drag is not one a double-click may resize either. The
    // pinned controls column is the case: it is sized to the controls a row carries, and widening it
    // would only take room off the columns that hold the data.
    if (table.getColumn(columnId)?.getCanResize() === false) return;

    const expanded = expandedColumns.current.has(columnId) || isHeldByReader?.(columnId) === true;
    const container = containerRef.current ?? table.refs.tableContainerRef.current;
    const contentWidth = expanded ? 0 : measureColumnContentWidth(container, columnId);
    const toggled = toggleColumnWidth({
      expanded,
      contentWidth,
      currentWidth: table.getState().columnSizing[columnId],
    });

    if (toggled.expanded) expandedColumns.current.add(columnId);
    else expandedColumns.current.delete(columnId);

    table.setColumnSizing((sizing) => applyColumnWidth(sizing, columnId, toggled.width));

    // The pair's first click reached the column's sort label on its way here, and a width toggle is
    // not a request to reorder the rows. Whatever the sort was before that click is put back.
    const sortingBefore = interaction.current.sortingBeforeClick;
    interaction.current = { ...interaction.current, sortingBeforeClick: undefined };
    if (sortingBefore !== undefined) table.setSorting(sortingBefore);
  }, [containerRef, isHeldByReader]);

  const headCellProps = useCallback(
    (columnId: string, table: MRT_TableInstance<TData>) => ({
      ...columnWidthCellProps(columnId),
      onClickCapture: (event: ReactMouseEvent<HTMLElement>) => {
        if (interaction.current.suppressNextClick) {
          interaction.current = { ...interaction.current, suppressNextClick: false };
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (isSecondClickOfDoubleClick(event.detail)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        interaction.current = { ...interaction.current, sortingBeforeClick: table.getState().sorting };
      },
      onDoubleClick: () => toggleWidth(columnId, table),
      onTouchEnd: () => {
        const tap = readDoubleTap(interaction.current.doubleTap, { columnId, at: Date.now() });
        interaction.current = { ...interaction.current, doubleTap: tap.next };
        if (!tap.isDoubleTap) return;
        // The tap that completed the pair still becomes a click, and that click would sort the column
        // behind the toggle. It is swallowed above rather than here, because it has not happened yet.
        interaction.current = { ...interaction.current, suppressNextClick: true };
        toggleWidth(columnId, table);
      },
    }),
    [toggleWidth],
  );

  const bodyCellProps = useCallback((columnId: string) => columnWidthCellProps(columnId), []);

  return { containerRef, headCellProps, bodyCellProps };
}
